<script lang="ts">
  import type { Snippet } from "svelte"
  import type { HTMLButtonAttributes } from "svelte/elements"
  import { KIND } from "./kind"
  import { SIZE } from "./size"

  let {
    as = "button",
    kind = KIND.PRIMARY,
    disabled = false,
    size,
    children,
    ...props
  }: {
    as?: string
    kind?: KIND
    size?: SIZE
    disabled?: boolean
    children?: Snippet
  } & HTMLButtonAttributes = $props()

  let isButton = $derived(as == "button")
  let isSecondary = $derived(kind === KIND.SECONDARY)
</script>

<!-- Boolean() wrapper is needed to prevent prettier from shortening the class declaration and eliminating the external customization comment -->
<svelte:element
  this={as}
  {...{
    ...props,
    disabled: isButton ? disabled : undefined,
  }}
  role={isButton ? undefined : "button"}
  tabindex={isButton ? undefined : 0}
  class={[
    ["button", { disabled, secondary: isSecondary }], // anchors for external customization
    "relative overflow-hidden",
    (size === SIZE.SMALL && "small") ||
      (size === SIZE.MEDIUM && "medium") ||
      (size === SIZE.LARGE && "large"),
    "before:absolute before:inset-0 before:opacity-0 before:transition-opacity enabled:focus:active:before:opacity-15",
    isSecondary ? "before:bg-white" : "before:bg-black",
    props.class,
  ]}
>
  {@render children?.()}
</svelte:element>

<style lang="postcss">
  * {
    @apply flex min-h-[3rem] cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-[14px] px-6 py-3 font-medium transition-colors;

    &.disabled {
      @apply cursor-not-allowed;
    }

    /* Primary styles by default */
    @apply primary;
  }

  .small,
  :global([data-ui-scale="xs"]) *:not(.small):not(.medium):not(.large),
  :global([data-ui-scale="sm"]) *:not(.small):not(.medium):not(.large) {
    @apply min-h-[2.5rem] rounded-xl px-5 py-2.5 text-sm/4;
  }

  .large,
  :global([data-ui-scale="lg"]) *:not(.small):not(.medium):not(.large),
  :global([data-ui-scale="xl"]) *:not(.small):not(.medium):not(.large) {
    @apply min-h-[3.75rem] px-8 py-4;
  }

  .primary {
    @apply bg-white text-black has-hover:hover:bg-white/70;

    &.disabled {
      @apply bg-opacity-10 text-white/10;
    }
  }

  .secondary {
    @apply border border-white/20 bg-black/30 text-white backdrop-blur-[20px] has-hover:hover:border-white/70 has-hover:hover:bg-black/60;

    &.disabled {
      @apply text-opacity-20;
    }
  }

  /* TODO: Update Svelte to v4, https://svelte.dev/docs/v4-migration-guide#other-breaking-changes */
  /* https://stackoverflow.com/questions/74085350/backdrop-filter-doesnt-apply-if-parents-opacity-is-less-than-1 */
  :global([inert]) .secondary {
    @apply backdrop-blur-none;
  }
</style>
